import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Radio, Trophy, Medal, ChevronDown, ExternalLink, Users, Youtube } from "lucide-react";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";

const tz = { timeZone: "America/Toronto", hour: "numeric", minute: "2-digit" };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-CA", tz); } catch { return ""; } };

function Card({ n, label, testid }) {
  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 text-center" data-testid={testid}>
      <div className="text-3xl font-extrabold text-[#0B0B0C]">{n}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#777] mt-1">{label}</div>
    </div>
  );
}

const STATUS = {
  champion: { label: "Champion", cls: "bg-[#F5A623]/15 text-[#8a5a00] border-[#F5A623]/40" },
  undefeated: { label: "Undefeated", cls: "bg-green-50 text-green-700 border-green-200" },
  one_loss: { label: "One loss", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  eliminated: { label: "Eliminated", cls: "bg-[#F4F5F3] text-[#999] border-[#E5E7EB]" },
  in: { label: "Still in", cls: "bg-blue-50 text-[#1E63C4] border-blue-200" },
};

function PlayersSection({ players }) {
  const [open, setOpen] = useState(true);
  if (!players || players.length === 0) return null;
  return (
    <section data-testid="live-players-list">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full mb-3"
        data-testid="live-players-list-toggle"
      >
        <h2 className="font-heading font-extrabold text-xl flex items-center gap-2"><Users className="h-5 w-5 text-[#1E63C4]" /> Players ({players.length})</h2>
        <ChevronDown className={`h-5 w-5 text-[#777] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="grid gap-2 sm:grid-cols-2">
          {players.map((p, i) => {
            const st = STATUS[p.status] || STATUS.in;
            return (
              <div key={i} className="flex items-center gap-2 bg-white rounded-lg border border-[#E5E7EB] p-2.5" data-testid={`live-player-${i}`}>
                {p.seed != null && <span className="h-6 w-6 shrink-0 rounded-full bg-[#F4F5F3] text-[#777] text-xs font-bold flex items-center justify-center">{p.seed}</span>}
                <span className="font-semibold text-sm text-[#0B0B0C] truncate flex-1">{p.name}</span>
                <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 whitespace-nowrap ${st.cls}`}>{st.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ScoreRow({ m }) {
  const p1w = m.winner && m.winner === m.player1;
  const p2w = m.winner && m.winner === m.player2;
  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-3">
      <div className={`flex justify-between text-sm ${p1w ? "font-bold text-[#0B0B0C]" : "text-[#555]"}`}><span>{m.player1 || "TBD"}</span><span>{(m.scores || "").split("-")[0]?.trim()}</span></div>
      <div className={`flex justify-between text-sm mt-1 ${p2w ? "font-bold text-[#0B0B0C]" : "text-[#555]"}`}><span>{m.player2 || "TBD"}</span><span>{(m.scores || "").split("-")[1]?.trim()}</span></div>
    </div>
  );
}

export default function LiveTournament() {
  const { ltid } = useParams();
  const [d, setD] = useState(undefined);
  const [feedN, setFeedN] = useState(6);
  const [showBracket, setShowBracket] = useState(false);
  const [stale, setStale] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/challonge/live/${ltid}`);
      setD(data);
      const age = data.last_updated ? (Date.now() - new Date(data.last_updated).getTime()) / 1000 : 9999;
      setStale(data.state !== "complete" && age > 300);
    } catch { setD(null); }
  }, [ltid]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 25000); // heartbeat: refresh local cache only
    return () => clearInterval(timer.current);
  }, [load]);

  if (d === undefined) return <Layout><div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#1E63C4]" /></div></Layout>;
  if (d === null) return <Layout><div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-6"><h1 className="font-heading font-bold text-2xl">No live tournament</h1><p className="text-[#777] mt-2">This tournament isn't being shown live right now.</p></div></Layout>;

  const complete = d.state === "complete";
  const s = d.summary;

  return (
    <Layout>
      {/* Header */}
      <section className="bg-[#0B0B0C] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {complete ? (
            <span className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide" data-testid="live-complete-badge"><Trophy className="h-4 w-4 text-[#F5A623]" /> Tournament Complete</span>
          ) : (
            <span className="inline-flex items-center gap-2 bg-[#E11D2A] rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide" data-testid="live-badge">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" /></span> Live
            </span>
          )}
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-3">{d.name}</h1>
          <p className="text-gray-300 mt-1 text-sm capitalize">{d.type}</p>
          {!complete && d.livestream_url && (
            <a
              href={d.livestream_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="live-watch-stream"
              className="inline-flex items-center gap-2 mt-4 bg-[#E11D2A] hover:bg-[#c41320] text-white rounded-md px-5 py-2.5 font-bold text-sm transition-colors shadow-lg shadow-[#E11D2A]/30"
            >
              <Youtube className="h-5 w-5" /> Watch Live Stream <ExternalLink className="h-4 w-4 opacity-80" />
            </a>
          )}
          <p className="text-gray-400 text-xs mt-2">Last updated: {d.last_updated ? fmtTime(d.last_updated) : "—"} · Results update automatically ~every 2 minutes.</p>
          {stale && <p className="text-amber-300 text-xs mt-1" data-testid="live-stale">Live results are temporarily delayed.</p>}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {complete && d.standings.length > 0 && (
          <section data-testid="live-final-standings">
            <h2 className="font-heading font-extrabold text-2xl mb-4">Final Standings</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {d.standings.slice(0, 8).map((p) => (
                <div key={p.rank} className="flex items-center gap-3 bg-white rounded-lg border border-[#E5E7EB] p-3">
                  <span className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm ${p.rank === 1 ? "bg-[#F5A623] text-white" : p.rank <= 3 ? "bg-[#E5E7EB]" : "bg-[#F4F5F3] text-[#777]"}`}>{p.rank}</span>
                  <span className="font-semibold">{p.name}</span>
                  {p.rank === 1 && <Trophy className="h-4 w-4 text-[#F5A623] ml-auto" />}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Summary */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card n={s.remaining} label="Players Remaining" testid="sum-remaining" />
          {d.is_double && <Card n={s.undefeated} label="Undefeated" testid="sum-undefeated" />}
          {d.is_double && <Card n={s.one_loss} label="One Loss" testid="sum-oneloss" />}
          <Card n={s.matches_complete} label="Matches Complete" testid="sum-matches" />
        </section>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {!complete && d.up_next.length > 0 && (
              <section data-testid="live-upnext">
                <h2 className="font-heading font-extrabold text-xl mb-3">Up Next</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {d.up_next.map((m, i) => (
                    <div key={i} className="bg-white rounded-lg border border-[#E5E7EB] p-3 text-sm">
                      <p className="font-semibold">{m.player1 || "TBD"} <span className="text-[#999]">vs.</span> {m.player2 || "TBD"}</p>
                      <p className="text-xs text-[#999] mt-1">Match {m.identifier} · Round {m.round}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section data-testid="live-latest">
              <h2 className="font-heading font-extrabold text-xl mb-3">Latest Results</h2>
              {d.latest_results.length === 0 ? <p className="text-[#999] text-sm">No completed matches yet.</p> : (
                <div className="grid sm:grid-cols-2 gap-3">{d.latest_results.map((m, i) => <ScoreRow key={i} m={m} />)}</div>
              )}
            </section>

            <PlayersSection players={d.players} />

            <section data-testid="live-feed">
              <h2 className="font-heading font-extrabold text-xl mb-3">Live Updates</h2>
              <div className="space-y-2">
                {d.feed.slice(0, feedN).map((f, i) => (
                  <div key={i} className="bg-white rounded-lg border border-[#E5E7EB] p-3">
                    <div className="flex justify-between gap-2"><span className="text-xs font-bold uppercase tracking-wide text-[#1E63C4]">{f.title}</span><span className="text-xs text-[#999]">{fmtTime(f.ts)}</span></div>
                    <p className="text-sm text-[#333] mt-1">{f.body}</p>
                  </div>
                ))}
                {d.feed.length > feedN && <button onClick={() => setFeedN((n) => n + 10)} className="text-sm text-[#1E63C4] font-semibold inline-flex items-center gap-1" data-testid="live-feed-more">Show more <ChevronDown className="h-4 w-4" /></button>}
              </div>
            </section>
          </div>

          <div className="space-y-8">
            {d.is_double && !complete && (
              <section data-testid="live-players">
                <h2 className="font-heading font-extrabold text-xl mb-3">Players Remaining</h2>
                {d.undefeated.length > 0 && <><p className="text-xs font-bold uppercase text-green-700 mb-1">Undefeated</p><div className="flex flex-wrap gap-1.5 mb-3">{d.undefeated.map((n) => <span key={n} className="text-xs bg-green-50 border border-green-200 rounded-full px-2.5 py-1">{n}</span>)}</div></>}
                {d.one_loss.length > 0 && <><p className="text-xs font-bold uppercase text-amber-700 mb-1">One Loss</p><div className="flex flex-wrap gap-1.5">{d.one_loss.map((n) => <span key={n} className="text-xs bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">{n}</span>)}</div></>}
              </section>
            )}
            {d.recently_eliminated.length > 0 && (
              <section data-testid="live-eliminated">
                <h2 className="font-heading font-extrabold text-xl mb-3">Recently Eliminated</h2>
                <div className="flex flex-wrap gap-1.5">{d.recently_eliminated.map((n) => <span key={n} className="text-xs bg-[#F4F5F3] border border-[#E5E7EB] rounded-full px-2.5 py-1 text-[#777]">{n}</span>)}</div>
              </section>
            )}
          </div>
        </div>

        {/* Bracket */}
        <section data-testid="live-bracket">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading font-extrabold text-xl">Full Bracket</h2>
            <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-sm text-[#1E63C4] font-semibold inline-flex items-center gap-1">Open on Challonge <ExternalLink className="h-4 w-4" /></a>
          </div>
          {showBracket ? (
            <div className="rounded-lg overflow-hidden border border-[#E5E7EB] bg-white">
              <iframe src={`https://challonge.com/${d.slug}/module`} title="Challonge bracket" width="100%" height="560" frameBorder="0" scrolling="auto" loading="lazy" />
            </div>
          ) : (
            <button onClick={() => setShowBracket(true)} className="w-full bg-white border border-[#E5E7EB] rounded-lg py-8 text-sm text-[#1E63C4] font-semibold hover:bg-[#F9FAF8]" data-testid="live-bracket-load">Load full bracket</button>
          )}
        </section>
      </div>
    </Layout>
  );
}
